import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'
import zionDebug from '@fs/zion-debug'
import placeAPIErrors from './placeApiWarningMessages.json'
import suggestAPlaceAPIErrors from './suggestAPlaceApiWarningMessages.json'

const debug = zionDebug('standards:places:apiService')

let cacheBusterCounter = 1

export function isValidUrL(textval) {
  textval = encodeURI(textval)
  const urlregex = /^(ftp|http|https):\/\/[^ "]+$/
  return urlregex.test(textval)
}

function getHttpErrorMessage(httpError) {
  switch (httpError) {
    case 400:
    case 422:
      return i18n.t(
        'http-error-400-422.bad-params',
        "We're sorry. An unexpected error occurred. Please verify your entries and try again later."
      )
    case 404:
      return i18n.t(
        'http-error-404.bad-params',
        "We're sorry. The requested entry was not found. You may need to refresh your page or try again later."
      )
    case 423:
      return i18n.t(
        'http-error-423.locked-resource',
        'One or more fields cannot be modified because the place is Certified.'
      )
    case 500:
      return i18n.t(
        'http-error-500.place-service',
        "We're sorry. An unexpected error occurred. Please try again later."
      )
    case 503:
      return i18n.t(
        'http-error-503.place-service-busy',
        "We're sorry. The FamilySearch Place Service is too busy to process your request right now. Please try again later."
      )
    default:
      return i18n.t('places-app.general-error', 'An error occurred. Please try again later.')
  }
}

function getTranslationParameterConfig({ paramNames = [], parameters }) {
  if (!parameters?.length || !paramNames.length || parameters?.length !== paramNames?.length) {
    return {}
  }

  const parameterObj = parameters.reduce((paramsObject, paramValue, index) => {
    const paramName = paramNames[index]
    paramsObject[paramName] = paramValue
    return paramsObject
  }, {})

  return parameterObj
}

/**
 * Handles errors from the Place API and returns an appropriate error message.
 *
 *
 * @param {Object} err - The error object returned from the API call. It should
 * have a `response` property which contains the HTTP response from the API. The
 * `response` object should have a `status` property (HTTP status code) and a
 * `headers` property. The `headers` property should contain a `warning` field
 * which is a JSON string that represents the warning message from the API.
 *
 * @param {string} errorType - A string that indicates the type of the error. It
 * can be either 'suggestAPlace' or any other value. Depending on this value, the
 * function will use either `suggestAPlaceAPIErrors` or `placeAPIErrors` to find
 * the corresponding API error.
 *
 * @returns {string} errorMessage - The error message. If an API warning is found,
 * it generates an error message using the `i18n.t` function with the
 * `translationKey`, `message`, and `parameters` from the API warning. If no API
 * warning is found, it uses the HTTP error message.
 *
 */

export function getPlaceAPIErrorMessageFromStatusAndWarnHeader(err, errorType) {
  const httpErrorMessage = getHttpErrorMessage(err?.response?.status)
  let warning
  try {
    warning = JSON.parse(err?.response?.headers?.warning || null)
  } catch {
    warning = {}
  }

  // WARNING!!! Backend Error codes are not unique.
  // The client must match API code, message in english, and significant parameters.
  const { code, message, parameters } = warning || {}
  const apiErrors = errorType === 'suggestAPlace' ? suggestAPlaceAPIErrors : placeAPIErrors
  const apiWarning = apiErrors.find((apiErr) => {
    return (
      apiErr.code === Number(code) &&
      (!apiErr?.matchWarningText || message.indexOf(apiErr.matchWarningText) !== -1) &&
      (!apiErr?.paramNames?.length || parameters?.length === apiErr?.paramNames?.length)
    )
  })
  const errorMessage = apiWarning
    ? i18n.t(
        apiWarning.translationKey,
        apiWarning.message,
        getTranslationParameterConfig({ paramNames: apiWarning.paramNames, parameters })
      )
    : httpErrorMessage
  const logMessage = code && !apiWarning ? `Error:${code} Fallback:${errorMessage}` : `Error:${code} ${errorMessage}`
  debug(logMessage)
  return errorMessage
}

export function getBasePlaceServiceUrl(isEditMode = false) {
  if (isEditMode) {
    return '/service/standards/place/ws-write'
  }
  return '/service/standards/place/ws-ui'
}

export function getBaseDateServiceUrl(isEditMode = false) {
  if (isEditMode) {
    return '/service/standards/date/ws-write'
  }
  return '/service/standards/date/ws-ui'
}

export function getCacheBuster() {
  return new Date().getTime() + cacheBusterCounter++
}

export function logFetch({
  method = 'get',
  sPath,
  sParameters = '',
  sBody = {},
  sRspTimeMS,
  error = {},
  status,
  level = 1,
  xRequestId = '',
} = {}) {
  const logMethod = `method=${method}`
  const logSPath = sPath ? `sPath=${sPath}` : ''
  const logSParameters = sParameters ? `sParameters=${stringify(sParameters)}` : ''
  const logSBody = sBody ? `sBody=${stringify(sBody)}` : ''
  const logSBodyTrimmed = logSBody.length > 32 ? `${logSBody.substring(0, 32)}...} ` : logSBody
  const logSRspTimeMS = sRspTimeMS ? `sRspTimeMS=${sRspTimeMS}` : ''
  const logError = error ? `error=${stringify(error)}` : ''
  const logStatus = status ? `status=${status}` : ''
  const logXRequestId = xRequestId ? `xRequestId=${xRequestId}` : ''

  tempWindowFSRemoteLog(
    `sApp=places ${logMethod} ${logSPath} ${logSParameters} ${logSBodyTrimmed} ${logSRspTimeMS} ${logError} ${logStatus} ${logXRequestId}`,
    level
  )
}

export function getFullParentName(juris = {}, separator = ', ') {
  if (!juris.jurisdiction) return juris.name || ''
  return [juris.name, getFullParentName(juris.jurisdiction, separator)].join(separator)
}

export async function doFetchGet(...args) {
  const [url, config = { headers: getDefaultHeaders() }] = args
  const xRequestId = config?.headers['X-Request-ID']
  const startTime = window.performance.now()
  const pathParamSplit = url.split('?')
  const sPath = pathParamSplit[0]
  const sParameters = pathParamSplit[1] ? `?${pathParamSplit[1]}` : undefined
  debug(`apiService.doFetchGet: {url: ${url}, config: ${JSON.stringify(config)}}`)
  try {
    const resp = await axios.get(url, config)
    const sRspTimeMS = `${window.performance.now() - startTime}`
    const status = resp.errors ? 'ERROR' : resp.statusMessage
    const error = resp.errors ? JSON.stringify(resp.errors) : undefined
    const level = resp.errors ? 4 : 1
    logFetch({ sPath, sParameters, sBody: {}, sRspTimeMS, error, status, level, xRequestId })
    return resp
  } catch (err) {
    const status = err.response ? err.response.status : err.status
    const sRspTimeMS = `${window.performance.now() - startTime}`
    const error = err.message || err.statusMessage
    logFetch({
      sPath,
      sParameters,
      sRspTimeMS,
      error,
      status,
      level: 4,
      xRequestId,
    })
    throw err
  }
}

export async function doFetchPost(...args) {
  const [url, body, config = { headers: getDefaultHeaders() }] = args
  const xRequestId = config?.headers['X-Request-ID']
  const startTime = window.performance.now()
  const pathParamSplit = url.split('?')
  const sPath = pathParamSplit[0]
  const sParameters = pathParamSplit[1] ? `?${pathParamSplit[1]}` : undefined
  try {
    const resp = await axios.post(url, body, config)
    const sRspTimeMS = `${window.performance.now() - startTime}`
    const status = resp.errors ? 'ERROR' : resp.statusMessage
    const error = resp.errors ? JSON.stringify(resp.errors) : undefined
    const level = resp.errors ? 4 : 1
    logFetch({ method: 'post', sPath, sParameters, sBody: body, sRspTimeMS, error, status, level, xRequestId })
    return resp
  } catch (err) {
    const status = err.response ? err.response.status : err.status
    const sRspTimeMS = `${window.performance.now() - startTime}`
    const error = err.message || err.statusMessage
    logFetch({
      method: 'post',
      sPath,
      sParameters,
      sBody: body,
      sRspTimeMS,
      error,
      status,
      level: 4,
      xRequestId,
    })
    throw err
  }
}

export async function doFetchPut(...args) {
  const [url, body, config = { headers: getDefaultHeaders() }] = args
  const xRequestId = config?.headers['X-Request-ID']
  const startTime = window.performance.now()
  const pathParamSplit = url.split('?')
  const sPath = pathParamSplit[0]
  const sParameters = pathParamSplit[1] ? `?${pathParamSplit[1]}` : undefined
  try {
    const resp = await axios.put(...args)
    const sRspTimeMS = `${window.performance.now() - startTime}`
    const status = resp.errors ? 'ERROR' : resp.statusMessage
    const error = resp.errors ? JSON.stringify(resp.errors) : undefined
    const level = resp.errors ? 4 : 1
    logFetch({ method: 'put', sPath, sParameters, sBody: body, sRspTimeMS, error, status, level, xRequestId })
    return resp
  } catch (err) {
    const status = err.response ? err.response.status : err.status
    const sRspTimeMS = `${window.performance.now() - startTime}`
    const error = err.message || err.statusMessage
    logFetch({
      method: 'put',
      sPath,
      sParameters,
      sBody: body,
      sRspTimeMS,
      error,
      status,
      level: 4,
      xRequestId,
    })
    throw err
  }
}

export async function doFetchDelete(...args) {
  const [url, config = { headers: getDefaultHeaders() }] = args
  const xRequestId = config?.headers['X-Request-ID']
  const startTime = window.performance.now()
  const pathParamSplit = url.split('?')
  const sPath = pathParamSplit[0]
  const sParameters = pathParamSplit[1] ? `?${pathParamSplit[1]}` : undefined
  try {
    const resp = await axios.delete(url, config)
    const sRspTimeMS = `${window.performance.now() - startTime}`

    // Fix for PVT-178.
    // The following status checks are a work-around for the backend API.
    // They should be removed once the places backend error handling story is completed.
    // `https://www5.v1host.com/FH-V1/story.mvc/Summary?oidToken=Story:882650`
    if (resp.status >= 400) {
      if (resp.status === 400) {
        resp.errors = [{ key: 'ERROR_GENERIC' }]
      } else if (resp.status >= 400) {
        resp.errors = [{ key: String(resp.status) }]
      }
    }

    const status = resp.errors ? 'ERROR' : resp.statusMessage
    const error = resp.errors ? JSON.stringify(resp.errors) : undefined
    const level = resp.errors ? 4 : 1
    logFetch({ method: 'del', sPath, sParameters, sBody: {}, sRspTimeMS, error, status, level, xRequestId })
    return resp
  } catch (err) {
    const status = err.response ? err.response.status : err.status
    const sRspTimeMS = `${window.performance.now() - startTime}`
    const error = err.message || err.statusMessage
    logFetch({
      method: 'del',
      sPath,
      sParameters,
      sBody: {},
      sRspTimeMS,
      error,
      status,
      level: 4,
      xRequestId,
    })
    throw err
  }
}

export async function getDates(queryString) {
  const baseUrl = getBaseDateServiceUrl()
  const interpUrl = `${baseUrl}/dates/interp?text=${queryString}&noCache=true&locale=${i18n.language}`
  const response = await doFetchGet(`${interpUrl}`)

  // debug('API_DATES_DATA:', response.data.dates)
  return response.data.dates
}

let uniqueId = 0

export function getXRequestId() {
  const now = Date.now()
  return `${now}-${++uniqueId}-places-react`
}

export function getDefaultHeaders() {
  return {
    'Content-Type': 'application/standards-places-v2+json',
    Accept: 'application/json',
    'X-Request-ID': getXRequestId(),
  }
}

export function getSendMailAPIInfo() {
  const host = window.location.host
  switch (host) {
    case 'www.familysearch.org':
      return {
        url: '/service/messaging/send/api/snippets/controlled',
        origin: 'https://www.familysearch.org',
        recipientEmail: 'placefeedback@familysearch.org', // only valid address for production
      }
    case 'beta.familysearch.org':
      return {
        url: '/service/messaging/send/api/snippets/controlled',
        origin: 'https://beta.familysearch.org',
        recipientEmail: 'thorntonjn@familysearch.org', // whitelisted for testing on beta
      }
    case 'integration.familysearch.org':
      return {
        url: '/service/messaging/send/api/snippets/controlled',
        origin: 'https://integration.familysearch.org',
        recipientEmail: 'thorntonjn@familysearch.org', // whitelisted for integration
      }
    case 'localhost':
    case 'localhost:5000':
      return {
        url: '/service/messaging/send/api/snippets/controlled',
        origin: 'https://beta.familysearch.org',
        recipientEmail: 'thorntonjn@familysearch.org', // whitelisted for testing on beta
      }
    default:
      throw new Error(`An appropriate URL for messaging/send/api/snippets/controlled could not be found for ${host} !`)
  }
}

function safeStringify(obj) {
  try {
    return typeof stringify === 'function' ? stringify(obj) : JSON.stringify(obj)
  } catch (err) {
    try {
      return JSON.stringify({ m: `Failed to serialize data: ${String(err)}`, s: 1 })
    } catch {
      return '{"m":"unserializable log data","s":1}'
    }
  }
}

function isLocalHostLike(loc) {
  const h = loc?.hostname || ''
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0'
}

/**
 * Log a message to remote with a severity level.
 * @param {string|object} data         - Information to log to remote.
 * @param {number}        [severity=1] - Severity level (log = 1, info = 2, warn = 3, error = 4)
 */
export function tempWindowFSRemoteLog(data, severity = 1) {
  // SSR / non-browser: no-op
  if (typeof window === 'undefined') return

  try {
    const s = Math.min(4, Math.max(1, Number.isFinite(severity) ? severity : 1))

    const logObj = { m: data, s }

    const url = isLocalHostLike(window.location) ? '/splunk-logger' : '/research/places/splunk-logger'

    const payload = safeStringify(logObj)

    // Prefer sendBeacon for fire-and-forget if available
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
      return
    }

    // Fallback to fetch if available
    if (typeof fetch === 'function') {
      // Do not await; swallow network errors
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true, // helps during unload in some browsers
      }).catch(() => {})
      return
    }

    // Final fallback to axios if present
    if (typeof axios !== 'undefined' && typeof axios.post === 'function') {
      axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } }).catch(() => {})
    }
    // If no transport, silently no-op
  } catch (outerErr) {
    // Final safeguard: never throw
    try {
      console.error('[tempWindowFSRemoteLog] swallowed error', outerErr)
    } catch {
      /* noop */
    }
  }
}

/* eslint-disable */

// Functions to handle circular JSON structures taken from: https://github.com/isaacs/json-stringify-safe/blob/02cfafd45f06d076ac4bf0dd28be6738a07a72f9/stringify.js
export function stringify(obj, replacer, spaces, cycleReplacer) {
  return JSON.stringify(obj, serializer(replacer, cycleReplacer), spaces)
}

function serializer(replacer, cycleReplacer) {
  const stack = []
  const keys = []

  if (cycleReplacer == null)
    cycleReplacer = function (key, value) {
      if (stack[0] === value) return '[Circular ~]'
      return `[Circular ~.${keys.slice(0, stack.indexOf(value)).join('.')}]`
    }

  return function (key, value) {
    if (value instanceof Error) {
      value = stringifyErrorObj(value)
    }

    if (stack.length > 0) {
      const thisPos = stack.indexOf(this)
      ~thisPos ? stack.splice(thisPos + 1) : stack.push(this)
      ~thisPos ? keys.splice(thisPos, Infinity, key) : keys.push(key)
      if (~stack.indexOf(value)) value = cycleReplacer.call(this, key, value)
    } else stack.push(value)

    return replacer == null ? value : replacer.call(this, key, value)
  }
}

/**
 * Stringify Error objects. The properties by default are not enumerable and won't be stringified.
 * @param value - Error instance
 */
function stringifyErrorObj(value) {
  if (value instanceof Error) {
    const error = {}
    Object.getOwnPropertyNames(value).forEach(function (key) {
      error[key] = value[key]
    })
    return error
  }

  return value
}

/* eslint-enable */
